'use client'

import {
  type PolymorphicProps,
  type SlotComponentProps,
  useSlotProps,
} from '@mui/base'
import {
  type ChangeEvent,
  type ComponentProps,
  type ElementType,
  type JSX,
  useId,
  useState,
} from 'react'

import { IconStar, IconStarFilled } from '@redteclab/icons'

export const RATING_DEFAULT_MAX = 5

type RatingPropsOwnProps<RootComponentType extends ElementType = 'span'> = {
  /**
   * Accepts a function which returns a string value that provides a visible label for each radio input.
   * Must be provided by the caller using their i18n solution to ensure proper localization.
   * @param value The 1-based index of the star
   */
  getLabelText?: (value: number) => string

  /**
   * Maximum rating
   * @default 5
   */
  max?: number

  /**
   * Called when the user clicks on a star
   * @param value The 1-based index of the clicked star
   */
  onChange?: (event: ChangeEvent, value: number) => void

  /**
   * Removes all hover effects and pointer events
   */
  readOnly?: boolean

  ref?: ComponentProps<RootComponentType>['ref']

  slotProps?: {
    iconContainer?: SlotComponentProps<
      'span',
      NonNullable<unknown>,
      RatingOwnerState
    >
    root?: SlotComponentProps<
      RootComponentType,
      NonNullable<unknown>,
      RatingOwnerState
    >
  }

  slots?: {
    iconContainer?: ElementType
    root?: RootComponentType
  }

  /**
   * The rating value
   */
  value?: number
}

export type RatingOwnerState = {
  value: number
}

interface RatingTypeMap<RootComponentType extends ElementType = 'span'> {
  defaultComponent: RootComponentType
  props: RatingPropsOwnProps<RootComponentType>
}

export type RatingProps<
  RootComponentType extends ElementType = RatingTypeMap['defaultComponent'],
> = PolymorphicProps<RatingTypeMap<RootComponentType>, RootComponentType>

/**
 * Ratings provide insight regarding others' opinions and experiences
 */
export const Rating = <RootComponentType extends ElementType = 'span'>(
  props: RatingProps<RootComponentType>,
): JSX.Element => {
  const {
    getLabelText,
    max = RATING_DEFAULT_MAX,
    onChange: onChangeProp,
    readOnly,
    ref,
    slotProps = {},
    slots = {},
    value = 0,
    ...other
  } = props
  const [hoverValue, setHoverValue] = useState<number | null>(null)

  const items = Array.from({ length: max }).keys().toArray()

  const id = useId()

  const onChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newValue = Math.trunc(Number(event.currentTarget.value))

    onChangeProp?.(event, newValue)
  }

  const ratingValue = hoverValue ?? value

  const ownerState: RatingOwnerState = {
    value,
  }

  const Root = slots.root ?? 'span'
  const rootProps = useSlotProps({
    additionalProps: {
      /**
       * Explicitly pass `ref` to `additionalProps` so `useSlotProps` resolves it,
       * if it's in `restProps`, `rootProps.ref` will be resolved to `null`.
       */
      ref,
      role: readOnly ? undefined : 'radiogroup',
    },
    className: 'rating',
    elementType: Root,
    externalForwardedProps: other,
    externalSlotProps: slotProps.root,
    ownerState,
  })

  const IconContainer = slots.iconContainer ?? 'span'
  const iconContainerProps = useSlotProps({
    className: readOnly ? 'rating__item' : undefined,
    elementType: IconContainer,
    externalSlotProps: slotProps.iconContainer,
    ownerState,
  })

  return (
    <Root {...rootProps}>
      {items.map((key) => {
        const star =
          key < ratingValue ? (
            <IconStarFilled className="rating__icon rating__icon_filled" />
          ) : (
            <IconStar className="rating__icon" />
          )

        if (readOnly) {
          return (
            <IconContainer
              {...iconContainerProps}
              key={key}
              value={typeof IconContainer === 'string' ? undefined : key + 1}
            >
              {star}
            </IconContainer>
          )
        }

        return (
          <label
            className="rating__item"
            key={key}
            onPointerEnter={(): void => {
              setHoverValue(key + 1)
            }}
            onPointerLeave={(): void => {
              setHoverValue(null)
            }}
          >
            <IconContainer
              {...iconContainerProps}
              value={typeof IconContainer === 'string' ? undefined : key + 1}
            >
              {star}
            </IconContainer>
            <input
              aria-label={getLabelText ? getLabelText(key + 1) : undefined}
              className="rating__input"
              id={`${id}_${key}`}
              name={id}
              onChange={onChange}
              type="radio"
              value={key + 1}
            />
          </label>
        )
      })}
    </Root>
  )
}
